import { createSelector, defaultMemoize } from 'reselect';
import { getIn } from 'icepick';
import { forEach, values, filter } from 'lodash';
import { updateTimeWithEventAndSelectedTimeZone } from '../modules/timezones';
import { DARK_RELEASE_VARIANT } from '../../constants/ExperimentConstants';

export const getSelectedMerchantAccountId = (event) => {
  return event.eventFeatureSetup.fees ? event.eventFeatureSetup.fees.merchantAccountId : null;
};

export const isFeesEnabled = (event) => {
  return event.eventFeatureSetup.fees ? event.eventFeatureSetup.fees.fees : false;
};

export const isInSitePaymentProcessingEnabled = (event) => {
  if (!isFeesEnabled(event)) {
    return false;
  }
  const merchantAccountId = event.eventFeatureSetup.fees.merchantAccountId;
  return merchantAccountId !== undefined && merchantAccountId !== null
    && merchantAccountId !== '00000000-0000-0000-0000-000000000000';
};

export const isDiscountCodesEnabled = (event) => {
  return event.eventFeatureSetup.fees ? event.eventFeatureSetup.fees.discounts : false;
};

export const isAdmissionItemsEnabled = (event) => {
  return event.eventFeatureSetup.agendaItems ?
    event.eventFeatureSetup.agendaItems.admissionItems : false;
};

export const getOptionalSessions = (event) => {
  return getIn(event, ['products', 'sessionContainer', 'optionalSessions']) || {};
};

export const getIncludedSessions = (event) => {
  return getIn(event, ['products', 'sessionContainer', 'includedSessions']) || {};
};

export const getSessionCategoryListOrders = (event) => {
  return getIn(event, ['products', 'sessionContainer', 'sessionCategoryListOrders']) || {};
};

const EMPTY_OBJECT = Object.freeze({});
export const getAdmissionItems = (state) => {
  return getIn(state, ['event', 'products', 'admissionItems']) || EMPTY_OBJECT;
};

export const sessionStatus = {
  active: 2,
  closed: 3,
  cancelled: 7
};

export const getAllSessions = createSelector(
  getOptionalSessions,
  getIncludedSessions,
  (optionalSessions, includeSessions) => {
    return { ...optionalSessions, ...includeSessions };
  });

export const getSessionGroups = (event) => {
  return getIn(event, ['products', 'sessionContainer', 'sessionGroups']) || {};
};

/* Retrieve all optional and included sessions and sessions within session groups */
export const getSessions = createSelector(
  getAllSessions,
  getSessionGroups,
  (sessionsContainer, groupSessionsContainer) => {
    const sessions = values(sessionsContainer);
    let groupSessions = [];
    forEach(groupSessionsContainer, group => {
      groupSessions = groupSessions.concat(values(group.sessions));
    });
    return [...sessions, ...groupSessions];
  }
);

const getStaffListToDisplay = defaultMemoize((staffList, translate) => {
  return staffList
    ? Object.values(staffList).filter(x => x.displayOnWebsite).map(staff => {
      return {
        ...staff,
        displayName: translate('EventWidgets_StaffName__resx', { firstName: staff.firstName, lastName: staff.lastName })
      };
    })
    : [];
});

/**
 * Returns staff list to display
 */
export const getStaffList = (event, translate) => {
  const { staffList } = event;
  return getStaffListToDisplay(staffList, translate);
};

const defaultSpeakerInfo = Object.freeze({});
const getSpeakerInfo = (event) => {
  return getIn(event, ['speakerInfoSnapshot']) || defaultSpeakerInfo;
};

export const getSpeakerCategoryOrder = createSelector(
  getSpeakerInfo,
  (speakerInfo) => {
    return speakerInfo.categoryOrder || {};
  }
);

const defaultSpeakers = Object.freeze({});
export const getSpeakers = createSelector(
  getSpeakerInfo,
  (speakerInfo) => {
    return speakerInfo.speakers || defaultSpeakers;
  }
);

export const getWebsiteSpeakers = createSelector(
  getSpeakers,
  (speakers) => {
    return filter(speakers, speaker => !!speaker.displayOnWebsite);
  }
);

/*
 * This is to check if waitlist is enabled at the event level and will be used
 * mainly by the site designer and the basis for guest side as well.
 * We added reg path level waitlist flag check at the guest side.
 */
export const isWaitlistEnabled =
  (event) => getIn(event, ['eventFeatureSetup', 'registrationProcess', 'eventWaitlist']) || false;

/*
 * This is to check if registration approval is enabled at the event level and will be used
 * mainly by the site designer and the basis for guest side as well.
 * We added reg path level registration aproval flag check at the guest side.
 */
export const isRegistrationApprovalEnabled =
  (event) => getIn(event, ['eventFeatureSetup', 'registrationProcess', 'registrationApproval']) || false;

/**
 * Gets the value for the guestRegistration flag, to know if Guest Registration is ON/OFF
 */
export const isGuestRegistrationEnabled =
  (event) => getIn(event, ['eventFeatureSetup', 'registrationProcess', 'guestRegistration']) || false;

export const adjustTimeZoneTimesForEvent = defaultMemoize((eventInfo, selectedTimeZone, eventTimezone) => {
  if (!selectedTimeZone || Object.keys(selectedTimeZone).length === 0) {
    return eventInfo;
  }
  const startDate = eventInfo.startDate ?
    updateTimeWithEventAndSelectedTimeZone(eventInfo.startDate, eventTimezone, selectedTimeZone) : null;
  const endDate = eventInfo.endDate ?
    updateTimeWithEventAndSelectedTimeZone(eventInfo.endDate, eventTimezone, selectedTimeZone) : null;
  const closeDate = eventInfo.closeDate ?
    updateTimeWithEventAndSelectedTimeZone(eventInfo.closeDate, eventTimezone, selectedTimeZone) : null;
  return { ...eventInfo, startDate, endDate, closeDate };
});

export const isProcessOfflineForCreditCardEnabled = (event, paymentSettings, experimentSettings) => {
  return (isFeesEnabled(event) && paymentSettings.creditCard.processOffline &&
    experimentSettings?.flexProductVersion >= DARK_RELEASE_VARIANT);
};
